import { FusionCustomInput } from 'components';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import CustomSlider from 'components/Sliders/CustomSlider';
import BaseTooltip from 'components/Tooltips/BaseTooltip';
import { nwsAlertTypes } from 'features/Weather/features/WeatherAlerts/config/nwsAlertTypes.config';
import {
  getAlertSeverityColor,
  getAlertSeverityLabel,
} from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import moment from 'moment';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectRescAlertDraft,
  selectTimeInputMode,
} from '../../state/selectors/alertCreatorSelectors';
import { setRescAlertDraft } from '../../state/slices/alertCreatorSlice';

const alertCreatorSelectClassNames = {
  control: () => 'alert-creator-select-control',
};

const RescAlertFields = () => {
  const rescAlertDraft = useSelector(selectRescAlertDraft);
  const timeInputMode = useSelector(selectTimeInputMode);
  const dispatch = useDispatch();

  const alertTypeOptions = nwsAlertTypes.map((alertType) => ({
    label: alertType,
    value: alertType,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    dispatch(
      setRescAlertDraft({
        ...rescAlertDraft,
        [name]: value,
      }),
    );
  };

  const handleTimeString = (e) => {
    const { name, value } = e.target;
    const date = new Date(value);

    dispatch(
      setRescAlertDraft({
        ...rescAlertDraft,
        [name]: date.toISOString(),
      }),
    );
  };

  return (
    <>
      <fieldset className="alert-creator-section border rounded-3 p-3 shadow-sm">
        <legend className="alert-creator-section-title d-flex align-items-center">
          Subscriber message
        </legend>

        <div className="alert-creator-grid align-items-stretch">
          <div className="alert-type-control h-100 border rounded-3 p-3">
            <BaseSelect
              inputId="weather-alert-creator-type"
              wrapperClassName="alert-creator-select flex-grow-full"
              label="Alert type *"
              isSearchable
              isClearable
              classNames={alertCreatorSelectClassNames}
              selectProps={{
                name: 'type',
                options: alertTypeOptions,
                value: findSelectOption(alertTypeOptions, rescAlertDraft.type),
                placeholder: 'Select an alert type',
                onChange: (option) =>
                  dispatch(
                    setRescAlertDraft({
                      ...rescAlertDraft,
                      type: option?.value ?? '',
                    }),
                  ),
                'aria-required': true,
              }}
            />
          </div>

          <div
            className="alert-severity-control h-100 border rounded-3 p-3"
            style={{
              '--alert-severity-color': getAlertSeverityColor(rescAlertDraft.severity),
            }}
          >
            <div className="alert-severity-header">
              <span className="alert-creator-control-label">Severity</span>
              <span className="alert-severity-badge" aria-live="polite">
                {getAlertSeverityLabel(rescAlertDraft.severity)}
              </span>
            </div>

            <CustomSlider
              name="severity"
              min={0}
              max={100}
              step={1}
              value={0 + rescAlertDraft.severity}
              onChange={(value) =>
                dispatch(
                  setRescAlertDraft({
                    ...rescAlertDraft,
                    severity: 0 + value,
                  }),
                )
              }
            />
          </div>
        </div>

        <FusionCustomInput
          controlId="rescalert-alert-headline"
          className="alert-creator-input-shell"
          label="Headline"
          required
          fullWidth
        >
          <Form.Control
            className="alert-creator-control"
            name="headline"
            value={rescAlertDraft.headline}
            onChange={handleChange}
            placeholder="Headline *"
            required
          />
        </FusionCustomInput>

        <FusionCustomInput
          controlId="rescalert-alert-description"
          className="alert-creator-input-shell"
          label="Description"
          required
          fullWidth
        >
          <Form.Control
            as="textarea"
            className="alert-creator-control"
            name="description"
            value={rescAlertDraft.description}
            onChange={handleChange}
            placeholder="Description *"
            rows={4}
            required
          />
        </FusionCustomInput>
      </fieldset>

      <fieldset className="alert-creator-section border rounded-3 p-3 shadow-sm">
        <legend className="alert-creator-section-title d-flex align-items-center">
          Audience details
        </legend>

        <div className="alert-creator-grid">
          <FusionCustomInput
            controlId="rescalert-alert-state"
            className="alert-creator-input-shell"
            label="State"
            fullWidth
          >
            <Form.Control
              className="alert-creator-control"
              name="state"
              value={rescAlertDraft.state}
              onChange={handleChange}
              placeholder="State"
            />
          </FusionCustomInput>

          <FusionCustomInput
            controlId="rescalert-alert-city"
            className="alert-creator-input-shell"
            label="City"
            fullWidth
          >
            <Form.Control
              className="alert-creator-control"
              name="city"
              value={rescAlertDraft.city}
              onChange={handleChange}
              placeholder="City"
            />
          </FusionCustomInput>
        </div>

        <div className="alert-creator-option-row d-flex flex-wrap align-items-center gap-3 border rounded-3 p-3">
          <Form.Check
            type="switch"
            id="alert-creator-internal-use"
            name="isInternalUse"
            label="Internal Use Only"
            checked={!!rescAlertDraft.isInternalUse}
            onChange={(e) =>
              dispatch(
                setRescAlertDraft({
                  ...rescAlertDraft,
                  isInternalUse: e.target.checked,
                }),
              )
            }
          />

          <BaseTooltip
            id="alert-creator-internal-use-tooltip"
            text="Internal Use Only does not send public alerts out to users — the alert is only visible to internal tooling."
          >
            <button
              type="button"
              aria-label="Internal Use Only does not send public alerts out to users — the alert is only visible to internal tooling."
              className="alert-creator-help-link"
            >
              Details
            </button>
          </BaseTooltip>
        </div>
      </fieldset>

      {timeInputMode === 'setTime' && (
        <fieldset className="alert-creator-section border rounded-3 p-3 shadow-sm">
          <legend className="alert-creator-section-title d-flex align-items-center">
            Delivery timing
          </legend>

          <div className="alert-creator-grid">
            <FusionCustomInput
              controlId="rescalert-alert-issued-time"
              className="alert-creator-input-shell"
              label="Start Time"
              required
              fullWidth
            >
              <Form.Control
                className="alert-creator-control"
                type="datetime-local"
                name="issuedTime"
                value={moment(rescAlertDraft.issuedTime).format('YYYY-MM-DDTHH:mm')}
                onChange={handleTimeString}
                placeholder="Start Time"
              />
            </FusionCustomInput>

            <FusionCustomInput
              controlId="rescalert-alert-expiration-time"
              className="alert-creator-input-shell"
              label="Expiration Time"
              required
              fullWidth
            >
              <Form.Control
                className="alert-creator-control"
                type="datetime-local"
                name="expirationTime"
                value={moment(rescAlertDraft.expirationTime).format('YYYY-MM-DDTHH:mm')}
                onChange={handleTimeString}
                placeholder="Expiration Time"
              />
            </FusionCustomInput>
          </div>
        </fieldset>
      )}
    </>
  );
};

export default RescAlertFields;
